import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Avatar,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Badge,
  ToggleButton,
  ToggleButtonGroup,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Dashboard,
  Class,
  Assignment,
  Message,
  School,
  CheckCircle,
  Schedule,
  Add,
  Send,
  Groups,
  AssignmentTurnedIn,
  EventAvailable,
  Close,
  Folder
} from '@mui/icons-material';
import ResourcesTab from '../components/ResourcesTab';
import apiClient from '../services/apiClient';

const TeacherPortal = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [user, setUser] = useState(null);
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [assignmentDialog, setAssignmentDialog] = useState(false);
  const [newAssignment, setNewAssignment] = useState({ title: '', dueDate: '', description: '', points: '' });
  const [attendance, setAttendance] = useState({});
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [messageText, setMessageText] = useState('');
  const [messageTo, setMessageTo] = useState('');
  const [messageSubject, setMessageSubject] = useState('');

  const [assignments, setAssignments] = useState([
    { id: 1, title: 'Chapter 4 Test', dueDate: '2026-05-20', submitted: 0, total: 0, points: 100, status: 'active' },
    { id: 2, title: 'Homework Set 15', dueDate: '2026-05-15', submitted: 0, total: 0, points: 20, status: 'active' },
  ]);

  const [messages] = useState([
    { id: 1, from: 'Administration', subject: 'Staff Meeting — May 12', time: 'Today', read: false, body: 'Please join us for a staff meeting on Monday, May 12 at 3:30 PM in the conference room.' },
    { id: 2, from: 'Principal', subject: 'Lesson plan submission reminder', time: 'Yesterday', read: true, body: 'Friendly reminder to submit your lesson plans for next quarter by Friday.' },
  ]);

  useEffect(() => {
    const stored = localStorage.getItem('teacherUser');
    if (!stored) {
      navigate('/login', { replace: true });
      return;
    }
    const teacherData = JSON.parse(stored);
    setUser(teacherData);

    const loadStudents = async () => {
      setStudentsLoading(true);
      try {
        const result = await apiClient.getUsers({ role: 'student', limit: 200 });
        const list = result.users || result || [];
        const mapped = list.map(s => ({
          id: s.user_id,
          name: `${s.first_name} ${s.last_name}`,
          email: s.email,
          status: s.status || 'active',
        }));
        setStudents(mapped);
        const initialAttendance = {};
        mapped.forEach(s => { initialAttendance[s.id] = 'present'; });
        setAttendance(initialAttendance);
        setAssignments(prev => prev.map(a => ({ ...a, total: mapped.length })));
      } catch (err) {
        console.error('Failed to load students:', err);
      } finally {
        setStudentsLoading(false);
      }
    };

    loadStudents();
  }, [navigate]);

  const handleSignOut = () => {
    localStorage.removeItem('teacherUser');
    localStorage.removeItem('authToken');
    localStorage.removeItem('userRole');
    navigate('/login');
  };

  const handleAttendanceChange = (studentId, status) => {
    if (status !== null) setAttendance(prev => ({ ...prev, [studentId]: status }));
  };

  const handleSaveAttendance = () => {
    const presentCount = Object.values(attendance).filter(v => v === 'present').length;
    setSnackbar({ open: true, message: `Attendance saved — ${attendanceDate} (${presentCount}/${students.length} present)`, severity: 'success' });
  };

  const handleAddAssignment = () => {
    if (!newAssignment.title || !newAssignment.dueDate) return;
    const created = {
      id: assignments.length + 1,
      ...newAssignment,
      submitted: 0,
      total: students.length,
      status: 'active'
    };
    setAssignments(prev => [...prev, created]);
    setNewAssignment({ title: '', dueDate: '', description: '', points: '' });
    setAssignmentDialog(false);
    setSnackbar({ open: true, message: 'Assignment added successfully', severity: 'success' });
  };

  const handleSendMessage = () => {
    if (!messageText.trim() || !messageTo) return;
    setMessageText('');
    setMessageSubject('');
    setMessageTo('');
    setSnackbar({ open: true, message: 'Message sent successfully', severity: 'success' });
  };

  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const displayName = `${user.firstName} ${user.lastName}`;
  const unreadCount = messages.filter(m => !m.read).length;

  return (
    <Box>
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ width: 52, height: 52, bgcolor: 'rgba(255,255,255,0.2)', fontSize: '1.4rem' }}>
                {displayName.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{displayName}</Typography>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>{user.email}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Badge badgeContent={unreadCount} color="error">
                <Schedule sx={{ color: 'white' }} />
              </Badge>
              <Button
                variant="outlined"
                onClick={handleSignOut}
                sx={{ borderColor: 'white', color: 'white', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
              >
                Sign Out
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Summary strip */}
        <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip
              icon={<Groups />}
              label={studentsLoading ? 'Loading…' : `${students.length} Students`}
              color="primary"
              variant="outlined"
            />
            <Chip icon={<Assignment />} label={`${assignments.filter(a => a.status === 'active').length} Active Assignments`} variant="outlined" />
            <Chip icon={<Message />} label={`${unreadCount} Unread Messages`} color={unreadCount > 0 ? 'error' : 'default'} variant="outlined" />
          </Box>
        </Paper>

        {/* Tabs */}
        <Paper elevation={1} sx={{ mb: 3, borderRadius: 2 }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab icon={<Dashboard />} label="Dashboard" iconPosition="start" />
            <Tab icon={<Groups />} label="Gradebook" iconPosition="start" />
            <Tab icon={<EventAvailable />} label="Attendance" iconPosition="start" />
            <Tab icon={<Assignment />} label="Assignments" iconPosition="start" />
            <Tab icon={<Message />} label={<Badge badgeContent={unreadCount} color="error">Messages</Badge>} iconPosition="start" />
            <Tab icon={<Folder />} label="Resources" iconPosition="start" />
          </Tabs>
        </Paper>

        {/* Dashboard Tab */}
        {activeTab === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ textAlign: 'center', py: 2.5 }}>
                  {studentsLoading
                    ? <CircularProgress size={32} />
                    : <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>{students.length}</Typography>
                  }
                  <Typography variant="body2" color="text.secondary">Total Students</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ textAlign: 'center', py: 2.5 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main' }}>{assignments.filter(a => a.status === 'active').length}</Typography>
                  <Typography variant="body2" color="text.secondary">Active Assignments</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ textAlign: 'center', py: 2.5 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.main' }}>{unreadCount}</Typography>
                  <Typography variant="body2" color="text.secondary">Unread Messages</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ textAlign: 'center', py: 2.5 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                    {students.filter(s => s.status === 'active').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Active Students</Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Recent Activity</Typography>
                  <List dense disablePadding>
                    {[
                      { text: 'Students loaded from database', icon: <Groups sx={{ fontSize: 16, color: 'primary.main' }} /> },
                      { text: `${assignments.filter(a => a.status === 'active').length} assignments active`, icon: <AssignmentTurnedIn sx={{ fontSize: 16, color: 'warning.main' }} /> },
                      { text: 'New message from Administration', icon: <Message sx={{ fontSize: 16, color: 'warning.main' }} /> },
                      { text: 'Attendance ready to record', icon: <EventAvailable sx={{ fontSize: 16, color: 'info.main' }} /> },
                    ].map((item, i) => (
                      <ListItem key={i} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>{item.icon}</ListItemIcon>
                        <ListItemText primary={item.text} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Active Assignments</Typography>
                  <List dense disablePadding>
                    {assignments.filter(a => a.status === 'active').map((a, i) => (
                      <ListItem key={i} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}><Schedule sx={{ fontSize: 16, color: 'warning.main' }} /></ListItemIcon>
                        <ListItemText primary={a.title} secondary={`Due ${a.dueDate} · ${a.points} pts`} />
                      </ListItem>
                    ))}
                    {assignments.filter(a => a.status === 'active').length === 0 && (
                      <Typography variant="body2" color="text.secondary">No active assignments</Typography>
                    )}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Gradebook Tab */}
        {activeTab === 1 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Student Roster — Gradebook</Typography>
            {studentsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : (
              <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Student Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {students.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          No students enrolled yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      students.map(student => (
                        <TableRow key={student.id} hover>
                          <TableCell sx={{ fontWeight: 500 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light', fontSize: '0.85rem' }}>
                                {student.name.charAt(0)}
                              </Avatar>
                              {student.name}
                            </Box>
                          </TableCell>
                          <TableCell>{student.email}</TableCell>
                          <TableCell>
                            <Chip
                              label={student.status}
                              color={student.status === 'active' ? 'success' : 'default'}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* Attendance Tab */}
        {activeTab === 2 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Attendance</Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField type="date" label="Date" value={attendanceDate} onChange={(e) => setAttendanceDate(e.target.value)} size="small" InputLabelProps={{ shrink: true }} />
                <Button variant="contained" onClick={handleSaveAttendance} startIcon={<CheckCircle />} disabled={students.length === 0}>Save Attendance</Button>
              </Box>
            </Box>
            {studentsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : (
              <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Student Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {students.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          No students enrolled yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      students.map(student => (
                        <TableRow key={student.id} hover>
                          <TableCell sx={{ fontWeight: 500 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light', fontSize: '0.85rem' }}>
                                {student.name.charAt(0)}
                              </Avatar>
                              {student.name}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <ToggleButtonGroup
                              value={attendance[student.id] || 'present'}
                              exclusive
                              onChange={(e, val) => handleAttendanceChange(student.id, val)}
                              size="small"
                            >
                              <ToggleButton value="present" color="success" sx={{ px: 2 }}>Present</ToggleButton>
                              <ToggleButton value="absent" color="error" sx={{ px: 2 }}>Absent</ToggleButton>
                              <ToggleButton value="tardy" color="warning" sx={{ px: 2 }}>Tardy</ToggleButton>
                              <ToggleButton value="excused" sx={{ px: 2 }}>Excused</ToggleButton>
                            </ToggleButtonGroup>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* Assignments Tab */}
        {activeTab === 3 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Assignments</Typography>
              <Button variant="contained" startIcon={<Add />} onClick={() => setAssignmentDialog(true)}>Add Assignment</Button>
            </Box>
            <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Assignment</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Due Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Points</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Students</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignments.map(a => (
                    <TableRow key={a.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{a.title}</TableCell>
                      <TableCell>{a.dueDate}</TableCell>
                      <TableCell>{a.points} pts</TableCell>
                      <TableCell>{a.total} students</TableCell>
                      <TableCell>
                        <Chip label={a.status === 'graded' ? 'Graded' : 'Active'} color={a.status === 'graded' ? 'success' : 'primary'} size="small" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Messages Tab */}
        {activeTab === 4 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Inbox</Typography>
                  {messages.map(msg => (
                    <Box key={msg.id} sx={{ p: 2, mb: 1.5, borderRadius: 1, bgcolor: msg.read ? 'transparent' : 'primary.50', border: '1px solid', borderColor: msg.read ? 'divider' : 'primary.200' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: msg.read ? 400 : 700 }}>{msg.from}</Typography>
                        <Typography variant="caption" color="text.secondary">{msg.time}</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: msg.read ? 400 : 600, mb: 0.5 }}>{msg.subject}</Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>{msg.body}</Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Compose Message</Typography>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>To</InputLabel>
                    <Select value={messageTo} label="To" onChange={(e) => setMessageTo(e.target.value)}>
                      {students.map(s => (
                        <MenuItem key={s.id} value={s.email}>
                          {s.name}
                        </MenuItem>
                      ))}
                      <MenuItem value="principal">Principal</MenuItem>
                      <MenuItem value="all">All Students</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Subject" fullWidth sx={{ mb: 2 }} size="small"
                    value={messageSubject}
                    onChange={(e) => setMessageSubject(e.target.value)}
                  />
                  <TextField
                    label="Message"
                    multiline rows={5} fullWidth sx={{ mb: 2 }}
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                  />
                  <Button variant="contained" fullWidth startIcon={<Send />} onClick={handleSendMessage} disabled={!messageTo || !messageText.trim()}>
                    Send Message
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Resources Tab */}
        {activeTab === 5 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>School Resources</Typography>
            <ResourcesTab role="teacher" />
          </Box>
        )}
      </Container>

      {/* Add Assignment Dialog */}
      <Dialog open={assignmentDialog} onClose={() => setAssignmentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Add New Assignment
          <IconButton onClick={() => setAssignmentDialog(false)} size="small"><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Assignment Title" fullWidth value={newAssignment.title} onChange={(e) => setNewAssignment(p => ({ ...p, title: e.target.value }))} required />
            <TextField type="date" label="Due Date" fullWidth InputLabelProps={{ shrink: true }} value={newAssignment.dueDate} onChange={(e) => setNewAssignment(p => ({ ...p, dueDate: e.target.value }))} required />
            <TextField label="Total Points" type="number" fullWidth value={newAssignment.points} onChange={(e) => setNewAssignment(p => ({ ...p, points: e.target.value }))} />
            <TextField label="Description / Instructions" multiline rows={3} fullWidth value={newAssignment.description} onChange={(e) => setNewAssignment(p => ({ ...p, description: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setAssignmentDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddAssignment} disabled={!newAssignment.title || !newAssignment.dueDate}>Add Assignment</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(p => ({ ...p, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default TeacherPortal;
